import { ThreadComment, DEFAULT_QUICK_EMOJIS, ReactionGroup, unifiedToNative } from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

const logger = createLogger('utils/reaction-helpers');

// Track processed message IDs to prevent duplicate handling
const processedMessageIds = new Set<string>();

// ============================================================================
// Default Emoji Constants
// ============================================================================

/**
 * Get the default emoji for quick reactions (thumbsup)
 */
export const getDefaultEmoji = (): string => {
  return DEFAULT_QUICK_EMOJIS[0]; // 1F44D (thumbsup)
};

/**
 * Get the list of default quick emojis
 */
export const getDefaultQuickEmojis = (): string[] => {
  return DEFAULT_QUICK_EMOJIS;
};

// ============================================================================
// Optimistic Update & Message Handling
// ============================================================================

/**
 * Creates an optimistic update for a comment emoji reaction toggle
 */
export function createOptimisticReactionUpdate(
  comment: ThreadComment & { isReactionProcessing?: boolean },
  emoji: string,
  currentUserId: string
) {
  const currentGroups = comment.reactionGroups || [];
  const existingGroup = currentGroups.find(g => g.emoji === emoji);
  const hasReaction = existingGroup?.hasReacted ?? false;
  const tempId = crypto.randomUUID();

  let updatedGroups: ReactionGroup[];
  if (hasReaction) {
    // Removing reaction
    updatedGroups = currentGroups
      .map(g => g.emoji === emoji
        ? { ...g, count: Math.max(0, g.count - 1), hasReacted: false }
        : g
      )
      .filter(g => g.count > 0);
  } else {
    // Adding reaction
    if (existingGroup) {
      updatedGroups = currentGroups.map(g =>
        g.emoji === emoji
          ? { ...g, count: g.count + 1, hasReacted: true }
          : g
      );
    } else {
      updatedGroups = [
        ...currentGroups,
        {
          emoji,
          nativeEmoji: unifiedToNative(emoji),
          count: 1,
          hasReacted: true,
          reactors: [],
        },
      ];
    }
  }

  const totalReactions = updatedGroups.reduce((sum, g) => sum + g.count, 0);

  // Create the optimistically updated comment
  const updatedComment = {
    ...comment,
    reactionGroups: updatedGroups,
    totalReactions,
    isReactionProcessing: true,
  };

  return {
    tempId,
    hasReaction,
    updatedComment,
  };
}

/**
 * Generate a unique, trackable message ID for broadcast events
 */
export function generateBroadcastMessageId(type: 'add' | 'remove', reactionId: string): string {
  const messageId = `reaction-${type === 'add' ? 'added' : 'removed'}-${reactionId}-${Date.now()}`;
  return messageId;
}

/**
 * Generate a stable message ID that will be the same across all tabs
 * for the same reaction event, preventing multi-broadcast loops
 */
export function generateStableMessageId(type: 'add' | 'remove', reactionId: string, targetId: string): string {
  // Create a consistent ID using the action type, reaction ID, and target (comment/thread) ID
  // This ensures the same event generates the same ID across all tabs
  return `stable-reaction-${type === 'add' ? 'added' : 'removed'}-${reactionId}-${targetId}`;
}

/**
 * Check if a message has already been processed to prevent duplicates
 */
export function hasProcessedMessage(messageId: string): boolean {
  if (!messageId) return false;
  return processedMessageIds.has(messageId);
}

/**
 * Mark a message as processed to prevent duplicates
 */
export function markMessageAsProcessed(messageId: string) {
  if (!messageId) return;

  processedMessageIds.add(messageId);

  // Clean up old message IDs to prevent memory leaks
  if (processedMessageIds.size > 500) {
    const iterator = processedMessageIds.values();
    for (let i = 0; i < 100; i++) {
      const value = iterator.next().value;
      if (value) processedMessageIds.delete(value);
    }
  }
}

/**
 * Get current user ID from storage
 */
export function getCurrentUserId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('currentUserId');
}

/**
 * Store current user ID for cross-tab identification
 */
export function storeCurrentUserId(userId: string): void {
  if (typeof window === 'undefined' || !userId) return;
  localStorage.setItem('currentUserId', userId);
}

/**
 * Cleanup helper to remove expired tracked items from local storage
 */
export function cleanupLocalStorageTracking() {
  if (typeof window === 'undefined') return;

  try {
    // Clean up reactions tracking
    const now = Date.now();
    const MAX_AGE = 3600000; // 1 hour

    // Clean up recent reactions
    try {
      const recentReactions = JSON.parse(localStorage.getItem('recentReactions') || '[]');
      const filteredReactions = recentReactions.filter((r: any) => now - r.timestamp < MAX_AGE);

      if (filteredReactions.length !== recentReactions.length) {
        localStorage.setItem('recentReactions', JSON.stringify(filteredReactions));
      }
    } catch (e) {
      logger.error('Error cleaning up recentReactions:', e);
    }

    // Clean up recently added comments
    try {
      const recentComments = JSON.parse(localStorage.getItem('recentlyAddedComments') || '[]');
      const filteredComments = recentComments.filter((c: any) => now - c.timestamp < MAX_AGE);

      if (filteredComments.length !== recentComments.length) {
        localStorage.setItem('recentlyAddedComments', JSON.stringify(filteredComments));
      }
    } catch (e) {
      logger.error('Error cleaning up recentlyAddedComments:', e);
    }
  } catch (e) {
    logger.error('Error in cleanupLocalStorageTracking:', e);
  }
}
